import { formatWithdrawalAmount } from '../features/withdrawals/region';
import { useState, useCallback, useEffect, useMemo, type ChangeEvent } from 'react';
import {
  useWithdrawalList,
  WITHDRAWAL_STATUS_LABELS,
  ALL_WITHDRAWAL_STATUSES,
  WITHDRAWAL_PAYOUT_PROVIDER_LABELS,
  ALL_WITHDRAWAL_PAYOUT_PROVIDER_FILTERS,
  WITHDRAWAL_SORT_ORDER_OPTIONS,
} from '../features/withdrawals';
import type {
  PayoutAttemptDTO,
  PayoutJobDTO,
  PayoutReconcileReportDTO,
  WithdrawalDTO,
  WithdrawalDetailDTO,
  WithdrawalStatus,
} from '../features/withdrawals';
import { useWithdrawalPolicy } from '../features/settings';
import { fenToYuan } from '../lib/format';
import { hasAdminPermission } from '../lib/adminPermissions';
import { ConfirmDialog } from '../components/feedback/ConfirmDialog';
import { normalizeImageValueForSubmit, uploadImageToCos, resolveImagePreviewUrl } from '../lib/upload/cos';
import { useAuthStore } from '../store/auth.store';

const STATUS_STYLES: Record<WithdrawalStatus, string> = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  paying: 'bg-sky-50 text-sky-700',
  payout_failed: 'bg-orange-50 text-orange-700',
  manual_processing: 'bg-blue-50 text-blue-700',
  rejected: 'bg-red-50 text-red-700',
  settled: 'bg-green-50 text-green-700',
};

const PAYOUT_STATUS_LABELS: Record<NonNullable<WithdrawalDTO['payout_status']>, string> = {
  '': '未进入自动打款',
  manual: '人工处理',
  queued: '待执行',
  processing: '处理中',
  succeeded: '三方成功待标记',
  failed: '已失败',
};

const PAYOUT_ACTION_LABELS: Record<string, string> = {
  create: '创建打款',
  query: '主动查单',
  callback: '回调回写',
};

const PAYOUT_JOB_TYPE_LABELS: Record<string, string> = {
  create: '创建任务',
  query: '查单任务',
};

const PAYOUT_JOB_STATUS_LABELS: Record<string, string> = {
  queued: '排队中',
  running: '执行中',
  succeeded: '已完成',
  failed: '已失败',
  cancelled: '已取消',
};

const RECONCILE_REASON_LABELS: Record<string, string> = {
  create_queue_stale: '待创建任务卡住',
  query_overdue: '处理中超时待查单',
  failed_needs_recheck: '失败单待复核',
  provider_succeeded_pending_manual_settlement: '三方成功待标记到账',
};

function canManualSettle(item: WithdrawalDTO): boolean {
  if (typeof item.can_manual_settle === 'boolean') return item.can_manual_settle;
  return item.status === 'approved'
    || item.status === 'manual_processing'
    || item.status === 'payout_failed'
    || (item.status === 'paying' && item.payout_mode === 'auto');
}

function canReject(item: WithdrawalDTO): boolean {
  if (typeof item.can_reject === 'boolean') return item.can_reject;
  return item.status === 'pending'
    || item.status === 'approved'
    || item.status === 'paying'
    || item.status === 'manual_processing'
    || item.status === 'payout_failed';
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

type ReviewDialogState =
  | { type: 'closed' }
  | { type: 'approve'; withdrawalIds: number[] }
  | { type: 'reject'; withdrawalIds: number[] };

type PayoutActionDialogState =
  | { type: 'closed' }
  | { type: 'start_payout'; item: WithdrawalDTO }
  | { type: 'query_payout'; item: WithdrawalDTO }
  | { type: 'retry_payout'; item: WithdrawalDTO }
  | { type: 'switch_manual'; item: WithdrawalDTO };

type BatchSettleDialogState =
  | { type: 'closed' }
  | { type: 'settle'; withdrawalIds: number[] };

const MAX_PAYMENT_PROOF_BYTES = 2 * 1024 * 1024;
const PAGE_SIZE_OPTIONS = [20, 50, 100, 500, 1000];

function formatChannel(channel: WithdrawalDTO['channel']): string {
  switch (channel) {
    case 'bank_card':
      return '银行卡';
    case 'alipay':
      return '支付宝';
    default:
      return '-';
  }
}

function formatAccountDetail(item: WithdrawalDTO): string {
  if (item.channel === 'bank_card') {
    return [item.bank_name, item.bank_card_number].filter(Boolean).join(' ') || item.target_summary || '-';
  }
  if (item.channel === 'alipay') {
    return item.alipay_account || item.target_summary || '-';
  }
  return item.target_summary || '-';
}

function formatSettlementNote(item: WithdrawalDTO): string {
  return item.settlement_note?.trim() || '-';
}

function formatPayoutMode(mode: WithdrawalDTO['payout_mode']): string {
  switch (mode) {
    case 'auto':
      return '自动打款';
    case 'manual':
      return '人工处理';
    default:
      return '-';
  }
}

function formatPayoutStatus(status: WithdrawalDTO['payout_status']): string {
  if (status == null) return '-';
  return PAYOUT_STATUS_LABELS[status] ?? status;
}

function formatPayoutFailureReason(item: WithdrawalDTO): string {
  const code = item.payout_last_error_code?.trim() ?? '';
  const message = item.payout_last_error_message?.trim() ?? '';
  if (code && message) return `${code} / ${message}`;
  return message || code || '-';
}

function formatPayload(payload: Record<string, unknown> | undefined): string {
  if (!payload || Object.keys(payload).length === 0) return '-';
  return JSON.stringify(payload, null, 2);
}

function formatAttemptAction(action: PayoutAttemptDTO['action']): string {
  return PAYOUT_ACTION_LABELS[action] ?? action;
}

function formatJobType(jobType: PayoutJobDTO['job_type']): string {
  return PAYOUT_JOB_TYPE_LABELS[jobType] ?? jobType;
}

function formatJobStatus(status: PayoutJobDTO['status']): string {
  return PAYOUT_JOB_STATUS_LABELS[status] ?? status;
}

function formatReconcileReason(reason: string | undefined): string {
  if (!reason) return '-';
  return RECONCILE_REASON_LABELS[reason] ?? reason;
}

interface SettlementDialogProps {
  open: boolean;
  item: WithdrawalDTO | null;
  loading: boolean;
  onConfirm: (payload: { settlementNote: string; paymentProofPath: string }) => void;
  onCancel: () => void;
}

function SettlementDialog({
  open,
  item,
  loading,
  onConfirm,
  onCancel,
}: SettlementDialogProps) {
  const [note, setNote] = useState('');
  const [paymentProofPath, setPaymentProofPath] = useState('');
  const [paymentProofPreviewUrl, setPaymentProofPreviewUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) {
      setNote('');
      setPaymentProofPath('');
      setPaymentProofPreviewUrl('');
      setFileName('');
      setError('');
      return;
    }
    setNote(item?.settlement_note ?? '');
    setPaymentProofPath(item?.payment_proof_data_url ?? '');
    setPaymentProofPreviewUrl('');
    setFileName('');
    setError('');
  }, [open, item]);

  const handleCancel = useCallback(() => {
    if (loading) return;
    onCancel();
  }, [loading, onCancel]);

  const handleFileChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('仅支持上传图片文件。');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_PAYMENT_PROOF_BYTES) {
      setError('打款截图需控制在 2MB 以内。');
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);
      const uploaded = await uploadImageToCos(file, 'withdrawal-proof');
      setPaymentProofPath(uploaded.relativePath);
      setPaymentProofPreviewUrl(uploaded.readUrl);
      setFileName(file.name);
      setError('');
    } catch {
      setError('上传打款截图失败，请重试。');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm({
      settlementNote: note.trim(),
      paymentProofPath: normalizeImageValueForSubmit(paymentProofPath.trim()),
    });
  }, [note, onConfirm, paymentProofPath]);

  if (!open || !item) return null;
  const isAutoPayoutArrival = item.payout_mode === 'auto' && item.status === 'paying';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={handleCancel} />
      <div className="relative w-full max-w-xl mx-4 rounded-xl bg-white p-6 shadow-xl space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {isAutoPayoutArrival ? '确认标记到账' : '确认标记已结算'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {isAutoPayoutArrival
              ? '请先确认三方查单已显示真实到账，再手工标记到账。'
              : '请核对收款信息，并可附上手工打款截图作为凭证。'}
          </p>
        </div>

        <div className="grid gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">用户</div>
            <div className="mt-1 text-sm font-medium text-gray-900">{item.nickname || '-'}</div>
            <div className="text-xs text-gray-500">{item.email || '-'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">金额</div>
            <div className="mt-1 text-sm font-medium text-gray-900">{formatWithdrawalAmount(item)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">收款人</div>
            <div className="mt-1 text-sm text-gray-900">{item.account_name || '-'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">渠道</div>
            <div className="mt-1 text-sm text-gray-900">{formatChannel(item.channel)}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-gray-400">收款账号</div>
            <div className="mt-1 break-all font-mono text-sm text-gray-900">{formatAccountDetail(item)}</div>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">打款备注</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            disabled={loading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            placeholder={isAutoPayoutArrival ? '可填写三方查单结果、到账确认说明等' : '可填写打款流水号、到账说明等'}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">打款截图</label>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handleFileChange}
            disabled={loading || uploading}
            className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary/15"
          />
          <div className="text-xs text-gray-400">支持 PNG / JPG / WebP / GIF，单张不超过 2MB。</div>
          {fileName && <div className="text-xs text-gray-500">已选择：{fileName}</div>}
          {error && <div className="text-sm text-red-500">{error}</div>}
          {uploading && <div className="text-xs text-gray-400">截图上传中...</div>}
          {paymentProofPath && (
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-3">
              <img
                src={resolveImagePreviewUrl(paymentProofPath, paymentProofPreviewUrl ? { [paymentProofPath]: paymentProofPreviewUrl } : undefined)}
                alt="打款凭证预览"
                className="h-24 w-24 rounded-md border border-gray-200 object-cover"
              />
              <div className="space-y-2">
                <a
                  href={resolveImagePreviewUrl(paymentProofPath, paymentProofPreviewUrl ? { [paymentProofPath]: paymentProofPreviewUrl } : undefined)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-primary hover:text-primary/80"
                >
                  查看大图
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setPaymentProofPath('');
                    setPaymentProofPreviewUrl('');
                    setFileName('');
                  }}
                  disabled={loading || uploading}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  清除截图
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || uploading || !!error}
            className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors flex items-center gap-2"
          >
            {loading && <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {isAutoPayoutArrival ? '标记到账' : '标记已结算'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface WithdrawalDetailDialogProps {
  open: boolean;
  detail: WithdrawalDetailDTO | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

function WithdrawalDetailDialog({
  open,
  detail,
  loading,
  error,
  onClose,
}: WithdrawalDetailDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative mx-4 flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">提现详情</h3>
            <p className="mt-1 text-sm text-gray-500">
              查看自动打款尝试、异步任务和当前提现状态。
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            关闭
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && !detail && (
            <div className="flex items-center justify-center py-20">
              <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {detail && (
            <div className="space-y-6">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">用户</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{detail.nickname || '-'}</div>
                  <div className="mt-1 text-xs text-gray-500 break-all">{detail.email || '-'}</div>
                  <div className="mt-1 text-[11px] text-gray-400">UID #{detail.user_id}</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">金额与渠道</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{formatWithdrawalAmount(detail)}</div>
                  <div className="mt-1 text-xs text-gray-500">{formatChannel(detail.channel)}</div>
                  <div className="mt-1 text-[11px] text-gray-400">{detail.account_name || '-'}</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">提现状态</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">
                    {WITHDRAWAL_STATUS_LABELS[detail.status as WithdrawalStatus] ?? detail.status}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{formatPayoutMode(detail.payout_mode)}</div>
                  <div className="mt-1 text-[11px] text-gray-400">{formatPayoutStatus(detail.payout_status)}</div>
                  {detail.status === 'payout_failed' && (
                    <div
                      className="mt-3 rounded-md border border-orange-200 bg-orange-50 px-2 py-1.5 text-xs leading-5 text-orange-700 break-words"
                      title={formatPayoutFailureReason(detail)}
                    >
                      原因：{formatPayoutFailureReason(detail)}
                    </div>
                  )}
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">自动打款</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{detail.payout_provider || '-'}</div>
                  <div className="mt-1 text-xs text-gray-500">重试次数 {detail.payout_retry_count ?? 0}</div>
                  <div className="mt-1 text-[11px] text-gray-400 break-all">{detail.payout_out_trade_no || '-'}</div>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-[1.2fr,0.8fr]">
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">收款与回写信息</h4>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-400">收款账号</div>
                      <div className="mt-1 break-all font-mono text-sm text-gray-900">{formatAccountDetail(detail)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-400">收款摘要</div>
                      <div className="mt-1 text-sm text-gray-900">{detail.target_summary || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-400">三方单号</div>
                      <div className="mt-1 break-all font-mono text-sm text-gray-900">{detail.provider_trade_no || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-400">渠道编码</div>
                      <div className="mt-1 text-sm text-gray-900">{detail.provider_channel_code || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-400">最近错误码</div>
                      <div className="mt-1 text-sm text-gray-900">{detail.payout_last_error_code || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-400">最近错误信息</div>
                      <div className="mt-1 text-sm text-gray-900">{detail.payout_last_error_message || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-400">审核备注</div>
                      <div className="mt-1 text-sm text-gray-900">{detail.review_note || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-400">打款备注</div>
                      <div className="mt-1 text-sm text-gray-900">{formatSettlementNote(detail)}</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">关键时间点</h4>
                  <div className="mt-4 space-y-3 text-sm text-gray-700">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">申请时间</span>
                      <span className="tabular-nums">{formatDateTime(detail.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">审核时间</span>
                      <span className="tabular-nums">{formatDateTime(detail.reviewed_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">打款发起时间</span>
                      <span className="tabular-nums">{formatDateTime(detail.payout_requested_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">最近查单时间</span>
                      <span className="tabular-nums">{formatDateTime(detail.last_query_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">下次重试时间</span>
                      <span className="tabular-nums">{formatDateTime(detail.next_retry_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">打款完成时间</span>
                      <span className="tabular-nums">{formatDateTime(detail.payout_finished_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">结算时间</span>
                      <span className="tabular-nums">{formatDateTime(detail.settled_at)}</span>
                    </div>
                  </div>

                  {detail.payment_proof_data_url && (
                    <div className="mt-5 rounded-lg border border-gray-200 p-3">
                      <div className="text-xs uppercase tracking-wider text-gray-400">打款凭证</div>
                      <div className="mt-3 flex items-center gap-3">
                        <img
                          src={detail.payment_proof_data_url}
                          alt="打款凭证"
                          className="h-20 w-20 rounded-md border border-gray-200 object-cover"
                        />
                        <a
                          href={detail.payment_proof_data_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-primary hover:text-primary/80"
                        >
                          查看大图
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <section className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-4">
                  <h4 className="text-sm font-semibold text-gray-900">最近打款尝试</h4>
                  <span className="text-xs text-gray-400">{detail.latest_attempts.length} 条</span>
                </div>
                {detail.latest_attempts.length === 0 ? (
                  <div className="pt-6 text-sm text-gray-400">暂无自动打款尝试记录</div>
                ) : (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                          <th className="px-3 py-2">时间</th>
                          <th className="px-3 py-2">动作</th>
                          <th className="px-3 py-2">结果</th>
                          <th className="px-3 py-2">状态</th>
                          <th className="px-3 py-2">错误</th>
                          <th className="px-3 py-2">请求</th>
                          <th className="px-3 py-2">响应</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.latest_attempts.map((attempt) => (
                          <tr key={attempt.id} className="border-b border-gray-100 align-top">
                            <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                              {formatDateTime(attempt.created_at)}
                            </td>
                            <td className="px-3 py-3 text-gray-900">
                              <div>{formatAttemptAction(attempt.action)}</div>
                              <div className="mt-1 text-xs text-gray-400">{attempt.provider || '-'}</div>
                            </td>
                            <td className="px-3 py-3">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${attempt.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                                {attempt.success ? '成功' : '失败'}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-600">{attempt.provider_status || '-'}</td>
                            <td className="px-3 py-3 text-xs text-gray-600">
                              <div>{attempt.error_code || '-'}</div>
                              <div className="mt-1 max-w-[220px] break-words text-gray-400">{attempt.error_message || '-'}</div>
                            </td>
                            <td className="px-3 py-3">
                              <pre className="max-h-36 overflow-auto rounded-md bg-gray-50 p-2 text-[11px] text-gray-700">
                                {formatPayload(attempt.request_payload)}
                              </pre>
                            </td>
                            <td className="px-3 py-3">
                              <pre className="max-h-36 overflow-auto rounded-md bg-gray-50 p-2 text-[11px] text-gray-700">
                                {formatPayload(attempt.response_payload)}
                              </pre>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-4">
                  <h4 className="text-sm font-semibold text-gray-900">最近异步任务</h4>
                  <span className="text-xs text-gray-400">{detail.recent_jobs.length} 条</span>
                </div>
                {detail.recent_jobs.length === 0 ? (
                  <div className="pt-6 text-sm text-gray-400">暂无自动打款任务记录</div>
                ) : (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                          <th className="px-3 py-2">任务</th>
                          <th className="px-3 py-2">状态</th>
                          <th className="px-3 py-2">可执行时间</th>
                          <th className="px-3 py-2">锁定信息</th>
                          <th className="px-3 py-2">重试</th>
                          <th className="px-3 py-2">最近错误</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.recent_jobs.map((job) => (
                          <tr key={job.id} className="border-b border-gray-100 align-top">
                            <td className="px-3 py-3 text-gray-900">
                              <div>{formatJobType(job.job_type)}</div>
                              <div className="mt-1 text-xs text-gray-400">Job #{job.id}</div>
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-600">{formatJobStatus(job.status)}</td>
                            <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500">{formatDateTime(job.available_at)}</td>
                            <td className="px-3 py-3 text-xs text-gray-600">
                              <div>{job.locked_by || '-'}</div>
                              <div className="mt-1 text-gray-400">{formatDateTime(job.locked_at)}</div>
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-600">{job.attempt_count}</td>
                            <td className="px-3 py-3 text-xs text-gray-600">
                              <div>{job.last_error_code || '-'}</div>
                              <div className="mt-1 max-w-[240px] break-words text-gray-400">{job.last_error_message || '-'}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface PayoutReconcileDialogProps {
  open: boolean;
  report: PayoutReconcileReportDTO | null;
  loading: boolean;
  running: boolean;
  error: string | null;
  staleMinutes: number;
  limit: number;
  queuedCounts: { create: number; processing: number; failed: number } | null;
  onChangeStaleMinutes: (value: number) => void;
  onChangeLimit: (value: number) => void;
  onRefresh: () => void;
  onRun: () => void;
  onOpenDetail: (withdrawalId: number) => void;
  onClose: () => void;
}

function PayoutReconcileDialog({
  open,
  report,
  loading,
  running,
  error,
  staleMinutes,
  limit,
  queuedCounts,
  onChangeStaleMinutes,
  onChangeLimit,
  onRefresh,
  onRun,
  onOpenDetail,
  onClose,
}: PayoutReconcileDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative mx-4 flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">自动打款对账</h3>
            <p className="mt-1 text-sm text-gray-500">
              查看 stale 自动打款单、本地未终态汇总与三方余额快照，并可立即触发补偿扫描。
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            关闭
          </button>
        </div>

        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <div className="grid gap-3 md:grid-cols-[180px,180px,1fr]">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500">超时阈值（分钟）</label>
              <input
                type="number"
                min={1}
                max={1440}
                value={staleMinutes}
                onChange={(e) => onChangeStaleMinutes(Math.max(1, Number(e.target.value) || 1))}
                disabled={loading || running}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500">返回数量上限</label>
              <input
                type="number"
                min={1}
                max={500}
                value={limit}
                onChange={(e) => onChangeLimit(Math.max(1, Number(e.target.value) || 1))}
                disabled={loading || running}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div className="flex items-end justify-start gap-3 md:justify-end">
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading || running}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {loading && <span className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />}
                刷新报告
              </button>
              <button
                type="button"
                onClick={onRun}
                disabled={loading || running}
                className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-40"
              >
                {running && <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
                立即补偿扫描
              </button>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-400">
            当前会扫描早于 {staleMinutes} 分钟且仍未完成的自动打款记录，最多返回 {limit} 条候选单。
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {queuedCounts && (
            <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              本次已补偿入队：create {queuedCounts.create} 条，处理中查单 {queuedCounts.processing} 条，失败复核 {queuedCounts.failed} 条。
            </div>
          )}

          {loading && !report && (
            <div className="flex items-center justify-center py-20">
              <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
            </div>
          )}

          {report && (
            <div className="space-y-6">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">未终态汇总</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{report.summary.outstanding_count} 笔</div>
                  <div className="mt-1 text-xs text-gray-500">&yen;{report.summary.outstanding_amount_yuan}</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">待执行</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{report.summary.approved_count} 笔</div>
                  <div className="mt-1 text-xs text-gray-500">&yen;{report.summary.approved_amount_yuan}</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">处理中</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{report.summary.paying_count} 笔</div>
                  <div className="mt-1 text-xs text-gray-500">&yen;{report.summary.paying_amount_yuan}</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">失败待复核</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{report.summary.failed_count} 笔</div>
                  <div className="mt-1 text-xs text-gray-500">&yen;{report.summary.failed_amount_yuan}</div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-400">三方余额</div>
                  <div className="mt-2 text-sm font-medium text-gray-900">{report.provider || '-'}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {report.balance ? `可用 ¥${report.balance.available_yuan}` : '余额快照不可用'}
                  </div>
                  <div className="mt-1 text-[11px] text-gray-400">
                    {report.balance ? `冻结 ¥${report.balance.frozen_yuan} / 总额 ¥${report.balance.total_yuan}` : '-'}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-[1fr,1fr]">
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">报告信息</h4>
                  <div className="mt-4 space-y-3 text-sm text-gray-700">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">生成时间</span>
                      <span className="tabular-nums">{formatDateTime(report.generated_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">超时阈值</span>
                      <span className="tabular-nums">{formatDateTime(report.stale_before)} 之前</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">返回上限</span>
                      <span className="tabular-nums">{report.limit}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">候选单数</span>
                      <span className="tabular-nums">{report.items.length}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">余额快照说明</h4>
                  {report.provider_balance_error ? (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                      获取三方余额失败：{report.provider_balance_error}
                    </div>
                  ) : (
                    <div className="mt-4 space-y-3 text-sm text-gray-700">
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-gray-500">可用余额</span>
                        <span className="tabular-nums">&yen;{report.balance?.available_yuan ?? '-'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-gray-500">冻结余额</span>
                        <span className="tabular-nums">&yen;{report.balance?.frozen_yuan ?? '-'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-gray-500">账户总额</span>
                        <span className="tabular-nums">&yen;{report.balance?.total_yuan ?? '-'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <section className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-4">
                  <h4 className="text-sm font-semibold text-gray-900">候选对账单</h4>
                  <span className="text-xs text-gray-400">{report.items.length} 条</span>
                </div>
                {report.items.length === 0 ? (
                  <div className="pt-6 text-sm text-gray-400">当前没有需要人工关注的 stale 自动打款记录</div>
                ) : (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                          <th className="px-3 py-2">用户</th>
                          <th className="px-3 py-2">金额</th>
                          <th className="px-3 py-2">状态</th>
                          <th className="px-3 py-2">对账原因</th>
                          <th className="px-3 py-2">参考时间</th>
                          <th className="px-3 py-2">最近错误</th>
                          <th className="px-3 py-2">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.items.map((item) => (
                          <tr key={item.id} className="border-b border-gray-100 align-top">
                            <td className="px-3 py-3">
                              <div className="text-gray-900">{item.nickname || '-'}</div>
                              <div className="mt-1 text-xs text-gray-500 break-all">{item.email || '-'}</div>
                              <div className="mt-1 text-[11px] text-gray-400">UID #{item.user_id}</div>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap tabular-nums text-gray-900">
                              {formatWithdrawalAmount(item)}
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-600">
                              <div>{WITHDRAWAL_STATUS_LABELS[item.status as WithdrawalStatus] ?? item.status}</div>
                              <div className="mt-1 text-gray-400">{formatPayoutMode(item.payout_mode)} / {formatPayoutStatus(item.payout_status)}</div>
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-600">{formatReconcileReason(item.reconcile_reason)}</td>
                            <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500">{formatDateTime(item.reference_at)}</td>
                            <td className="px-3 py-3 text-xs text-gray-600">
                              <div>{item.payout_last_error_code || '-'}</div>
                              <div className="mt-1 max-w-[220px] break-words text-gray-400">{item.payout_last_error_message || '-'}</div>
                            </td>
                            <td className="px-3 py-3">
                              <button
                                type="button"
                                onClick={() => onOpenDetail(item.id)}
                                className="text-sm text-primary hover:text-primary/80"
                              >
                                查看详情
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function WithdrawalsPage() {
  const user = useAuthStore((s) => s.user);
  const {
    marketId, setMarketId, items, total, pendingTotalCount, pendingTotalAmountYuan, page, pageSize, totalPages,
    userId, userKeyword, startTime, endTime,
    status, payoutProvider, order,
    loading, error, submitting,
    setPage, setPageSize, setUserId, setUserKeyword, setStartTime, setEndTime, setStatus, setPayoutProvider, setOrder, resetFilters, reload, review, reviewMany, settle, settleMany,
    loadDetail, queryPayout, startPayout, retryPayout, switchToManual, loadReconcileReport, runReconcile,
  } = useWithdrawalList();
  const {
    data: policy,
    loading: policyLoading,
    saving: policySaving,
    error: policyError,
    minimumAmountFen,
    setMinimumAmountFen,
    firstMinimumAmountFen,
    setFirstMinimumAmountFen,
    dailyLimit,
    setDailyLimit,
    windowEnabled,
    setWindowEnabled,
    windowStartTime,
    setWindowStartTime,
    windowEndTime,
    setWindowEndTime,
    save: savePolicy,
  } = useWithdrawalPolicy();

  const [reviewDialog, setReviewDialog] = useState<ReviewDialogState>({ type: 'closed' });
  const [payoutActionDialog, setPayoutActionDialog] = useState<PayoutActionDialogState>({ type: 'closed' });
  const [batchSettleDialog, setBatchSettleDialog] = useState<BatchSettleDialogState>({ type: 'closed' });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [settlementTarget, setSettlementTarget] = useState<WithdrawalDTO | null>(null);
  const [detailWithdrawalId, setDetailWithdrawalId] = useState<number | null>(null);
  const [detail, setDetail] = useState<WithdrawalDetailDTO | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailReloadToken, setDetailReloadToken] = useState(0);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileReport, setReconcileReport] = useState<PayoutReconcileReportDTO | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileRunning, setReconcileRunning] = useState(false);
  const [reconcileError, setReconcileError] = useState<string | null>(null);
  const [reconcileStaleMinutes, setReconcileStaleMinutes] = useState(30);
  const [reconcileLimit, setReconcileLimit] = useState(100);
  const [reconcileQueuedCounts, setReconcileQueuedCounts] = useState<{ create: number; processing: number; failed: number } | null>(null);
  const canReviewWithdrawals = hasAdminPermission(user, 'withdrawals.review');
  const canSettleWithdrawals = hasAdminPermission(user, 'withdrawals.settle');
  const canManageAdvancedWithdrawals = hasAdminPermission(user, 'withdrawals') || hasAdminPermission(user, 'settings');

  const pendingIds = useMemo(
    () => (canReviewWithdrawals ? items.filter((item) => item.status === 'pending').map((item) => item.id) : []),
    [canReviewWithdrawals, items],
  );
  const settleableIds = useMemo(
    () => (canSettleWithdrawals ? items.filter((item) => canManualSettle(item)).map((item) => item.id) : []),
    [canSettleWithdrawals, items],
  );
  const rejectableIds = useMemo(
    () => (canReviewWithdrawals ? items.filter((item) => canReject(item)).map((item) => item.id) : []),
    [canReviewWithdrawals, items],
  );
  const selectableIds = useMemo(
    () => Array.from(new Set([...pendingIds, ...rejectableIds, ...settleableIds])),
    [pendingIds, rejectableIds, settleableIds],
  );
  const selectedPendingIds = useMemo(
    () => selectedIds.filter((id) => pendingIds.includes(id)),
    [pendingIds, selectedIds],
  );
  const selectedRejectableIds = useMemo(
    () => selectedIds.filter((id) => rejectableIds.includes(id)),
    [rejectableIds, selectedIds],
  );
  const selectedSettleableIds = useMemo(
    () => selectedIds.filter((id) => settleableIds.includes(id)),
    [selectedIds, settleableIds],
  );
  const selectedPendingTotalAmountFen = useMemo(() => {
    if (selectedIds.length === 0) return 0;
    const selectedPendingSet = new Set(selectedPendingIds);
    return items.reduce((sum, item) => (
      selectedPendingSet.has(item.id) ? sum + (item.amount_minor ?? item.amount_fen) : sum
    ), 0);
  }, [items, selectedIds.length, selectedPendingIds]);
  const pendingSummaryLabel = selectedIds.length > 0 ? '选中审核中总额' : '审核中总额';
  const pendingSummaryAmountYuan = selectedIds.length > 0
    ? (marketId === 'VN' ? String(selectedPendingTotalAmountFen) : fenToYuan(selectedPendingTotalAmountFen))
    : pendingTotalAmountYuan;
  const pendingSummaryCount = selectedIds.length > 0 ? selectedPendingIds.length : pendingTotalCount;
  const allSelectableSelected = selectableIds.length > 0 && selectedIds.filter((id) => selectableIds.includes(id)).length === selectableIds.length;
  const rejectingPayingCount = useMemo(() => {
    if (reviewDialog.type !== 'reject') return 0;
    const rejectingIds = new Set(reviewDialog.withdrawalIds);
    return items.filter((item) => rejectingIds.has(item.id) && item.status === 'paying').length;
  }, [items, reviewDialog]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => selectableIds.includes(id)));
  }, [selectableIds]);

  const toggleSelected = useCallback((withdrawalId: number) => {
    setSelectedIds((prev) => (
      prev.includes(withdrawalId)
        ? prev.filter((id) => id !== withdrawalId)
        : [...prev, withdrawalId]
    ));
  }, []);

  const toggleCurrentPage = useCallback(() => {
    setSelectedIds((prev) => {
      if (allSelectableSelected) {
        return prev.filter((id) => !selectableIds.includes(id));
      }
      const next = new Set(prev);
      selectableIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [allSelectableSelected, selectableIds]);

  const handleRejectConfirm = useCallback((note: string) => {
    if (reviewDialog.type !== 'reject') return;
    if (reviewDialog.withdrawalIds.length === 1) {
      review(reviewDialog.withdrawalIds[0], 'reject', note);
    } else {
      reviewMany(reviewDialog.withdrawalIds, 'reject', note);
    }
    setSelectedIds([]);
    setReviewDialog({ type: 'closed' });
  }, [review, reviewDialog, reviewMany]);

  const handleCancel = useCallback(() => {
    setReviewDialog({ type: 'closed' });
  }, []);

  const handleSettlementConfirm = useCallback((payload: {
    settlementNote: string;
    paymentProofPath: string;
  }) => {
    if (!settlementTarget) return;
    void settle(settlementTarget.id, payload);
    setSettlementTarget(null);
  }, [settle, settlementTarget]);

  const handleBatchSettleConfirm = useCallback((note: string) => {
    if (batchSettleDialog.type !== 'settle') return;
    void settleMany(batchSettleDialog.withdrawalIds, {
      settlementNote: note,
    });
    setSelectedIds([]);
    setBatchSettleDialog({ type: 'closed' });
  }, [batchSettleDialog, settleMany]);

  const handlePayoutActionConfirm = useCallback(async () => {
    if (payoutActionDialog.type === 'closed') return;

    let success = false;
    switch (payoutActionDialog.type) {
      case 'start_payout':
        success = await startPayout(payoutActionDialog.item.id);
        break;
      case 'query_payout':
        success = await queryPayout(payoutActionDialog.item.id);
        break;
      case 'retry_payout':
        success = await retryPayout(payoutActionDialog.item.id);
        break;
      case 'switch_manual':
        success = await switchToManual(payoutActionDialog.item.id);
        break;
      default:
        success = false;
    }

    if (success) {
      setPayoutActionDialog({ type: 'closed' });
      if (detailWithdrawalId === payoutActionDialog.item.id) {
        setDetailReloadToken((value) => value + 1);
      }
    }
  }, [detailWithdrawalId, payoutActionDialog, queryPayout, retryPayout, startPayout, switchToManual]);

  const handleLoadReconcileReport = useCallback(async () => {
    setReconcileLoading(true);
    setReconcileError(null);
    setReconcileQueuedCounts(null);
    try {
      const result = await loadReconcileReport(reconcileStaleMinutes, reconcileLimit);
      setReconcileReport(result);
    } catch (err: unknown) {
      setReconcileError(err instanceof Error ? err.message : '加载对账报告失败');
    } finally {
      setReconcileLoading(false);
    }
  }, [loadReconcileReport, reconcileLimit, reconcileStaleMinutes]);

  const handleRunReconcile = useCallback(async () => {
    setReconcileRunning(true);
    setReconcileError(null);
    try {
      const result = await runReconcile(reconcileStaleMinutes, reconcileLimit);
      if (!result) return;
      setReconcileReport(result);
      setReconcileQueuedCounts({
        create: result.queued_create_jobs,
        processing: result.queued_processing_jobs,
        failed: result.queued_failed_jobs,
      });
    } finally {
      setReconcileRunning(false);
    }
  }, [reconcileLimit, reconcileStaleMinutes, runReconcile]);

  useEffect(() => {
    if (!reconcileOpen) {
      return;
    }
    void handleLoadReconcileReport();
    // 仅在弹层打开时自动加载一次；修改参数后由用户手动刷新。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconcileOpen]);

  useEffect(() => {
    if (detailWithdrawalId == null) {
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetail((current) => (current?.id === detailWithdrawalId ? current : null));

    void loadDetail(detailWithdrawalId)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetailError(err instanceof Error ? err.message : '加载详情失败');
      })
      .finally(() => {
        if (cancelled) return;
        setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [detailReloadToken, detailWithdrawalId, loadDetail]);

  const payoutActionDialogMeta = payoutActionDialog.type === 'closed'
    ? null
    : ({
      start_payout: {
        title: '确认重新自动打款',
        description: '仅用于人工处理或异常单补救。通过审核时系统会自动排队打款，正常单不需要再点这里。',
        confirmLabel: '重新自动打款',
        variant: 'primary' as const,
      },
      query_payout: {
        title: '确认立即查单',
        description: '会向三方服务商查询最新打款状态，并写入详情尝试记录；查单不会自动标记到账或扣减冻结余额。',
        confirmLabel: '立即查单',
        variant: 'primary' as const,
      },
      retry_payout: {
        title: '确认重试自动打款',
        description: '会取消当前活跃自动打款任务，重新生成一条新的自动打款任务并更新 out_trade_no。',
        confirmLabel: '重新排队',
        variant: 'primary' as const,
      },
      switch_manual: {
        title: '确认转人工处理',
        description: '会取消自动打款链路中的活跃任务，并将提现切换为人工处理中。',
        confirmLabel: '转人工',
        variant: 'danger' as const,
      },
    }[payoutActionDialog.type]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">管理用户提现申请。点“通过”后系统会按配置自动排队打款；异常单可查单、重试、转人工或手工标记到账。</p>

      {canManageAdvancedWithdrawals && marketId === 'CN' && (
        <section className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">提现策略设置</h2>
            <p className="mt-1 text-sm text-gray-500">
              在提现管理页直接调整首次提现、后续提现的最低额度和每日提现最大次数，保存后即时生效。
            </p>
          </div>
          {policy?.updated_at && (
            <div className="text-xs text-gray-400 text-right">
              <div>最后更新：{formatDateTime(policy.updated_at)}</div>
              {policy.updated_by != null && <div>操作人 ID：{policy.updated_by}</div>}
            </div>
          )}
        </div>

        {policyError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {policyError}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              首次提现最低额度（分）
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={firstMinimumAmountFen}
                onChange={(e) => setFirstMinimumAmountFen(Number(e.target.value) || 0)}
                disabled={policyLoading || policySaving}
                className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50"
              />
              <span className="text-xs text-gray-400 shrink-0">= ¥{fenToYuan(firstMinimumAmountFen)}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              后续提现最低额度（分）
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={minimumAmountFen}
                onChange={(e) => setMinimumAmountFen(Number(e.target.value) || 0)}
                disabled={policyLoading || policySaving}
                className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50"
              />
              <span className="text-xs text-gray-400 shrink-0">= ¥{fenToYuan(minimumAmountFen)}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              每日提现最大次数
            </label>
            <input
              type="number"
              min={0}
              value={dailyLimit}
              onChange={(e) => setDailyLimit(Number(e.target.value) || 0)}
              disabled={policyLoading || policySaving}
              className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex h-10 items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={windowEnabled}
              onChange={(e) => setWindowEnabled(e.target.checked)}
              disabled={policyLoading || policySaving}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-50"
            />
            启用提款时间限制
          </label>
          <label className="text-sm text-gray-700">
            开始时间
            <input
              type="time"
              value={windowStartTime}
              onChange={(e) => setWindowStartTime(e.target.value)}
              disabled={!windowEnabled || policyLoading || policySaving}
              className="ml-2 h-10 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50"
            />
          </label>
          <label className="text-sm text-gray-700">
            截止时间
            <input
              type="time"
              value={windowEndTime}
              onChange={(e) => setWindowEndTime(e.target.value)}
              disabled={!windowEnabled || policyLoading || policySaving}
              className="ml-2 h-10 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50"
            />
          </label>
          <span className="text-xs text-gray-400">
            {windowEnabled ? `北京时间 ${windowStartTime} 至 ${windowEndTime} 可提交，截止时间本身不开放` : '当前未限制提款提交时间'}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              void savePolicy();
            }}
            disabled={policyLoading || policySaving}
            className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {(policyLoading || policySaving) && (
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            保存并即时生效
          </button>
          <span className="text-xs text-gray-400">
            当前生效值：首次 ¥{fenToYuan(policy?.first_minimum_amount_fen ?? firstMinimumAmountFen)}，后续 ¥{fenToYuan(policy?.minimum_amount_fen ?? minimumAmountFen)}，每日 {policy?.daily_limit ?? dailyLimit} 次，{policy?.window_enabled ? `北京时间 ${windowStartTime}-${windowEndTime}` : '不限时间'}
          </span>
        </div>
        </section>
      )}

      {/* Filters */}
      <label className="flex items-center gap-3 text-sm font-medium text-gray-700">
        地区
        <select value={marketId} onChange={(event) => { setSelectedIds([]); setMarketId(event.target.value); }} className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white">
          <option value="CN">中国</option>
          <option value="VN">越南</option>
        </select>
      </label>
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
          placeholder="用户 ID"
          className="h-9 w-32 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <input
          type="text"
          value={userKeyword}
          onChange={(e) => setUserKeyword(e.target.value)}
          placeholder="用户名/邮箱/昵称"
          className="h-9 w-40 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部状态</option>
          {ALL_WITHDRAWAL_STATUSES.map((s) => (
            <option key={s} value={s}>{WITHDRAWAL_STATUS_LABELS[s]}</option>
          ))}
        </select>
        <select
          value={payoutProvider}
          onChange={(e) => setPayoutProvider(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">全部提现通道</option>
          {(marketId === 'VN' ? ['csjvnpay'] as const : ALL_WITHDRAWAL_PAYOUT_PROVIDER_FILTERS).map((provider) => (
            <option key={provider} value={provider}>{WITHDRAWAL_PAYOUT_PROVIDER_LABELS[provider]}</option>
          ))}
        </select>
        <select
          value={order}
          onChange={(e) => setOrder(e.target.value as typeof order)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {WITHDRAWAL_SORT_ORDER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          申请时间
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="h-9 w-44 rounded-md border border-gray-300 px-3 text-sm bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <span className="text-sm text-gray-400">至</span>
        <input
          type="datetime-local"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
          className="h-9 w-44 rounded-md border border-gray-300 px-3 text-sm bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
          aria-label="申请结束时间"
        />

        <button
          onClick={resetFilters}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          重置
        </button>
        <button
          onClick={reload}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          刷新
        </button>
        <select
          value={pageSize}
          onChange={(event) => setPageSize(Number(event.target.value))}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {PAGE_SIZE_OPTIONS.map((value) => (
            <option key={value} value={value}>每页 {value} 条</option>
          ))}
        </select>
        {canManageAdvancedWithdrawals && marketId === 'CN' && (
          <button
            onClick={() => setReconcileOpen(true)}
            className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
          >
            自动打款对账
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
        <span className="font-medium text-amber-900">{pendingSummaryLabel}</span>
        <span className="tabular-nums text-lg font-semibold text-amber-900">{marketId === 'VN' ? `${Number(pendingSummaryAmountYuan).toLocaleString('en-US')} ₫` : `¥${pendingSummaryAmountYuan}`} </span>
        <span className="text-amber-700">{pendingSummaryCount} 笔</span>
      </div>

      {(selectedPendingIds.length > 0 || selectedRejectableIds.length > 0 || selectedSettleableIds.length > 0) && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <span className="text-amber-800">
            已选择 {selectedIds.length} 条提现
            {selectedPendingIds.length > 0 && `，待审核 ${selectedPendingIds.length} 条`}
            {selectedRejectableIds.length > 0 && `，可拒绝 ${selectedRejectableIds.length} 条`}
            {selectedSettleableIds.length > 0 && `，可标记到账 ${selectedSettleableIds.length} 条`}
          </span>
          <div className="flex items-center gap-2">
            {selectedPendingIds.length > 0 && (
              <button
                onClick={() => {
                  reviewMany(selectedPendingIds, 'approve', '');
                  setSelectedIds([]);
                }}
                disabled={submitting}
                className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-40"
              >
                批量通过
              </button>
            )}
            {canReviewWithdrawals && selectedRejectableIds.length > 0 && (
              <button
                onClick={() => setReviewDialog({ type: 'reject', withdrawalIds: selectedRejectableIds })}
                disabled={submitting}
                className="rounded-md bg-red-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-40"
              >
                批量拒绝
              </button>
            )}
            {canSettleWithdrawals && selectedSettleableIds.length > 0 && (
              <button
                onClick={() => setBatchSettleDialog({ type: 'settle', withdrawalIds: selectedSettleableIds })}
                disabled={submitting}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-40"
              >
                批量标记到账
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button onClick={reload} className="text-sm text-primary hover:underline">重试</button>
        </div>
      )}

      {/* Loading */}
      {loading && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          {items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">暂无数据</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="w-10 px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allSelectableSelected}
                        disabled={selectableIds.length === 0}
                        onChange={toggleCurrentPage}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-40"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">收款信息</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">金额</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">订单ID</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">审核备注</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">打款备注 / 凭证</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">申请时间</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">审核时间</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">结算时间</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          disabled={(!selectableIds.includes(item.id)) || submitting}
                          onChange={() => toggleSelected(item.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-30"
                        />
                      </td>
                      <td className="px-4 py-3 min-w-[180px]">
                        <div className="text-gray-900 font-medium whitespace-nowrap">{item.nickname ?? '-'}</div>
                        <div className="mt-1 text-xs text-gray-500 break-all">{item.email ?? '-'}</div>
                        <div className="mt-1 text-[11px] text-gray-400">UID #{item.user_id}</div>
                      </td>
                      <td className="px-4 py-3 min-w-[280px]">
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-gray-900">{item.account_name || '-'}</div>
                          <div className="text-xs text-gray-500">{formatChannel(item.channel)}</div>
                          <div className="break-all font-mono text-xs text-gray-700">{formatAccountDetail(item)}</div>
                          {item.target_summary && (
                            <div className="text-[11px] text-gray-400">摘要：{item.target_summary}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                        {formatWithdrawalAmount(item)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[item.status as WithdrawalStatus] ?? 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {WITHDRAWAL_STATUS_LABELS[item.status as WithdrawalStatus] ?? item.status}
                        </span>
                        <div className="mt-1 text-[11px] text-gray-400">
                          {formatPayoutMode(item.payout_mode)} / {formatPayoutStatus(item.payout_status)}
                        </div>
                        {item.status === 'payout_failed' && (
                          <div
                            className="mx-auto mt-2 max-w-[220px] rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-left text-[11px] leading-4 text-orange-700 break-words"
                            title={formatPayoutFailureReason(item)}
                          >
                            原因：{formatPayoutFailureReason(item)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 min-w-[220px]">
                        <div
                          className="break-all font-mono text-xs text-gray-700"
                          title={item.payout_out_trade_no || '-'}
                        >
                          {item.payout_out_trade_no || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-[160px] truncate text-xs" title={item.review_note || '-'}>
                        {item.review_note || '-'}
                      </td>
                      <td className="px-4 py-3 min-w-[220px]">
                        <div className="space-y-2">
                          <div className="text-xs text-gray-500" title={item.settlement_note || '-'}>
                            {formatSettlementNote(item)}
                          </div>
                          {item.payment_proof_data_url ? (
                            <div className="flex items-center gap-3">
                              <img
                                src={item.payment_proof_data_url}
                                alt="打款凭证"
                                className="h-12 w-12 rounded-md border border-gray-200 object-cover"
                              />
                              <a
                                href={item.payment_proof_data_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs font-medium text-primary hover:text-primary/80"
                              >
                                查看截图
                              </a>
                            </div>
                          ) : (
                            <div className="text-[11px] text-gray-400">
                              {canManualSettle(item) ? '结算时可上传截图' : '-'}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
                        {formatDateTime(item.created_at)}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
                        {formatDateTime(item.reviewed_at)}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
                        {formatDateTime(item.settled_at)}
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <button
                            onClick={() => setDetailWithdrawalId(item.id)}
                            disabled={submitting}
                            className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
                          >
                            详情
                          </button>
                          {canReviewWithdrawals && item.status === 'pending' && (
                            <button
                              onClick={() => {
                                void review(item.id, 'approve', '');
                              }}
                              disabled={submitting}
                              className="text-sm text-green-600 hover:text-green-700 disabled:opacity-40 transition-colors"
                            >
                              通过
                            </button>
                          )}
                          {canReviewWithdrawals && canReject(item) && (
                            <button
                              onClick={() => setReviewDialog({ type: 'reject', withdrawalIds: [item.id] })}
                              disabled={submitting}
                              className="text-sm text-red-500 hover:text-red-600 disabled:opacity-40 transition-colors"
                            >
                              拒绝
                            </button>
                          )}
                          {canReviewWithdrawals && item.can_start_payout && item.payout_mode !== 'auto' && (
                            <button
                              onClick={() => setPayoutActionDialog({ type: 'start_payout', item })}
                              disabled={submitting}
                              className="text-sm text-emerald-600 hover:text-emerald-700 disabled:opacity-40 transition-colors"
                            >
                              重新自动打款
                            </button>
                          )}
                          {canReviewWithdrawals && item.can_query_payout && (
                            <button
                              onClick={() => setPayoutActionDialog({ type: 'query_payout', item })}
                              disabled={submitting}
                              className="text-sm text-sky-600 hover:text-sky-700 disabled:opacity-40 transition-colors"
                            >
                              查单
                            </button>
                          )}
                          {canReviewWithdrawals && item.can_retry_payout && (
                            <button
                              onClick={() => setPayoutActionDialog({ type: 'retry_payout', item })}
                              disabled={submitting}
                              className="text-sm text-amber-600 hover:text-amber-700 disabled:opacity-40 transition-colors"
                            >
                              重试自动打款
                            </button>
                          )}
                          {canReviewWithdrawals && item.can_switch_manual && (
                            <button
                              onClick={() => setPayoutActionDialog({ type: 'switch_manual', item })}
                              disabled={submitting}
                              className="text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-40 transition-colors"
                            >
                              转人工
                            </button>
                          )}
                          {canSettleWithdrawals && canManualSettle(item) && (
                            <button
                              onClick={() => setSettlementTarget(item)}
                              disabled={submitting}
                              className="text-sm text-primary hover:text-primary/80 disabled:opacity-40 transition-colors"
                            >
                              {item.payout_mode === 'auto' && item.status === 'paying' ? '标记到账' : '标记已结算'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2 text-sm text-gray-500">
            <span>共 {total} 条</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                上一页
              </button>
              <span className="text-sm tabular-nums">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        </>
      )}

      {/* Reject dialog */}
      <ConfirmDialog
        open={canReviewWithdrawals && reviewDialog.type === 'reject'}
        title="确认拒绝提现"
        description={rejectingPayingCount > 0
          ? `拒绝后用户余额将退回。本次包含 ${rejectingPayingCount} 条提现中记录，请确认三方未实际到账。`
          : '拒绝后用户余额将退回，请填写原因。'}
        confirmLabel="拒绝"
        variant="danger"
        loading={submitting}
        noteLabel="拒绝原因"
        noteRequired
        onConfirm={handleRejectConfirm}
        onCancel={handleCancel}
      />

      <ConfirmDialog
        open={canSettleWithdrawals && batchSettleDialog.type === 'settle'}
        title="确认批量标记到账"
        description={`将把已勾选的 ${batchSettleDialog.type === 'settle' ? batchSettleDialog.withdrawalIds.length : 0} 条提现标记为已到账/已结算。请确认这些提现已经完成打款。`}
        confirmLabel="批量标记到账"
        variant="primary"
        loading={submitting}
        noteLabel="到账备注"
        onConfirm={handleBatchSettleConfirm}
        onCancel={() => setBatchSettleDialog({ type: 'closed' })}
      />

      {/* Payout action dialog */}
      <ConfirmDialog
        open={canReviewWithdrawals && payoutActionDialog.type !== 'closed'}
        title={payoutActionDialogMeta?.title ?? '确认操作'}
        description={payoutActionDialogMeta?.description}
        confirmLabel={payoutActionDialogMeta?.confirmLabel ?? '确认'}
        variant={payoutActionDialogMeta?.variant ?? 'primary'}
        loading={submitting}
        onConfirm={() => {
          void handlePayoutActionConfirm();
        }}
        onCancel={() => setPayoutActionDialog({ type: 'closed' })}
      />

      {/* Settle dialog */}
      <SettlementDialog
        open={canSettleWithdrawals && settlementTarget != null}
        item={settlementTarget}
        loading={submitting}
        onConfirm={handleSettlementConfirm}
        onCancel={() => setSettlementTarget(null)}
      />

      {/* Detail dialog */}
      <WithdrawalDetailDialog
        open={detailWithdrawalId != null}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={() => setDetailWithdrawalId(null)}
      />

      <PayoutReconcileDialog
        open={canManageAdvancedWithdrawals && reconcileOpen}
        report={reconcileReport}
        loading={reconcileLoading}
        running={reconcileRunning}
        error={reconcileError}
        staleMinutes={reconcileStaleMinutes}
        limit={reconcileLimit}
        queuedCounts={reconcileQueuedCounts}
        onChangeStaleMinutes={setReconcileStaleMinutes}
        onChangeLimit={setReconcileLimit}
        onRefresh={() => {
          void handleLoadReconcileReport();
        }}
        onRun={() => {
          void handleRunReconcile();
        }}
        onOpenDetail={(withdrawalId) => {
          setReconcileOpen(false);
          setDetailWithdrawalId(withdrawalId);
        }}
        onClose={() => setReconcileOpen(false)}
      />
    </div>
  );
}
